import './FundingInfoStrip.css';

export type FundingInstrumentMeta = {
  fundingIntervalHours: number | null;
  upperFundingRate: number | null;
  lowerFundingRate: number | null;
};

export type FundingSummary = {
  exchange1CurrentRate: number | null;
  exchange2CurrentRate: number | null;
  exchange1NextFundingTimeMs: number | null;
  exchange2NextFundingTimeMs: number | null;
};

export type NextNetFunding = {
  netRate: number | null;
  nextSettlementMs: number | null;
  settlingBoth: boolean;
};

const formatFundingRatePct = (rate: number | null | undefined): string => {
  if (rate == null || !Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(2)}%`;
};

const formatFundingIntervalHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(hours) || hours <= 0) return '—';
  if (Math.abs(hours - Math.round(hours)) < 0.01) return `${Math.round(hours)}h`;
  return `${hours.toFixed(1)}h`;
};

const formatFundingCapRange = (
  upper: number | null | undefined,
  lower: number | null | undefined,
): string => {
  const up = upper != null && Number.isFinite(upper) ? `${(upper * 100).toFixed(2)}%` : '—';
  const lo = lower != null && Number.isFinite(lower) ? `${(lower * 100).toFixed(2)}%` : '—';
  return `${up} / ${lo}`;
};

const formatFundingCountdown = (nextMs: number | null | undefined, nowMs: number): string => {
  if (nextMs == null || !Number.isFinite(nextMs) || nextMs <= 0) return '—';
  const diff = nextMs - nowMs;
  if (diff <= 0) return '0h 0m';
  const totalMin = Math.floor(diff / 60_000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return `${hours}h ${minutes}m`;
};

const formatFundingUpdatedAgo = (updatedAtMs: number | null | undefined, nowMs: number): string => {
  if (updatedAtMs == null || !Number.isFinite(updatedAtMs) || updatedAtMs <= 0) return '—';
  const sec = Math.max(0, Math.floor((nowMs - updatedAtMs) / 1000));
  return `${sec}秒前`;
};

export type FundingInfoStripProps = {
  exchange1: string;
  exchange2: string;
  direction: string;
  fundingSummary: FundingSummary;
  fundingInstrumentMeta: {
    exchange1: FundingInstrumentMeta | null;
    exchange2: FundingInstrumentMeta | null;
  };
  nextNetFunding: NextNetFunding;
  fundingNowMs: number;
  fundingSummaryUpdatedAtMs: number | null;
  fundingRefreshLoading: boolean;
  fundingRefreshError: string | null;
  onRefresh: () => void;
  refreshDisabled: boolean;
  variant?: 'default' | 'chart' | 'header';
};

export function FundingInfoStrip({
  exchange1,
  exchange2,
  direction,
  fundingSummary,
  fundingInstrumentMeta,
  nextNetFunding,
  fundingNowMs,
  fundingSummaryUpdatedAtMs,
  fundingRefreshLoading,
  fundingRefreshError,
  onRefresh,
  refreshDisabled,
  variant = 'default',
}: FundingInfoStripProps) {
  return (
    <div
      className={`realtime-spread-funding-strip${variant === 'chart' ? ' is-chart' : ''}${variant === 'header' ? ' is-header' : ''}`}
      aria-label="资金费率结算"
    >
      <span className="realtime-spread-funding-item">
        <b>{exchange1}</b>
        <strong>{formatFundingRatePct(fundingSummary.exchange1CurrentRate)}</strong>
        <span className="realtime-spread-funding-meta">
          周期 {formatFundingIntervalHours(fundingInstrumentMeta.exchange1?.fundingIntervalHours)}
          {' · '}
          cap{' '}
          {formatFundingCapRange(
            fundingInstrumentMeta.exchange1?.upperFundingRate,
            fundingInstrumentMeta.exchange1?.lowerFundingRate,
          )}
          {' · '}
          距结算 {formatFundingCountdown(fundingSummary.exchange1NextFundingTimeMs, fundingNowMs)}
        </span>
      </span>
      <span className="realtime-spread-funding-item">
        <b>{exchange2}</b>
        <strong>{formatFundingRatePct(fundingSummary.exchange2CurrentRate)}</strong>
        <span className="realtime-spread-funding-meta">
          周期 {formatFundingIntervalHours(fundingInstrumentMeta.exchange2?.fundingIntervalHours)}
          {' · '}
          cap{' '}
          {formatFundingCapRange(
            fundingInstrumentMeta.exchange2?.upperFundingRate,
            fundingInstrumentMeta.exchange2?.lowerFundingRate,
          )}
          {' · '}
          距结算 {formatFundingCountdown(fundingSummary.exchange2NextFundingTimeMs, fundingNowMs)}
        </span>
      </span>
      <span className="realtime-spread-funding-sep" aria-hidden="true">
        |
      </span>
      <span className="realtime-spread-funding-item is-net">
        <b>{direction}</b>
        <strong className={nextNetFunding.netRate != null && nextNetFunding.netRate < 0 ? 'is-negative' : ''}>
          {formatFundingRatePct(nextNetFunding.netRate)}
        </strong>
        <span className="realtime-spread-funding-meta">
          距结算 {formatFundingCountdown(nextNetFunding.nextSettlementMs, fundingNowMs)}
          {nextNetFunding.settlingBoth ? ' · 双所' : ''}
        </span>
      </span>
      <span className="realtime-spread-funding-strip-actions">
        <span
          className={`realtime-spread-funding-updated${fundingRefreshError ? ' is-error' : ''}`}
          title={fundingRefreshError ?? undefined}
        >
          {formatFundingUpdatedAgo(fundingSummaryUpdatedAtMs, fundingNowMs)}
        </span>
        <button
          type="button"
          className="realtime-spread-funding-refresh-btn"
          onClick={onRefresh}
          disabled={refreshDisabled || fundingRefreshLoading}
          title={fundingRefreshError ?? '刷新当前费率与结算时间'}
        >
          {fundingRefreshLoading ? '刷新中…' : '刷新资费'}
        </button>
      </span>
    </div>
  );
}
